import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    const storedToken = sessionStorage.getItem("authToken");
    // A user without a token is a stale session from before auth tokens
    // existed — it can't call any protected API route, so treat it as
    // logged out rather than restoring a broken session.
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      connectSocketWhenReady();
    } else if (storedUser) {
      sessionStorage.removeItem("user");
    }
    setLoadingUser(false);
  }, []);

  const connectSocketWhenReady = async () => {
    try {
      console.log("📥 Importing socket module...");
      const socketModule = await import("../socket");
      console.log("✅ Socket module imported");
    } catch (err) {
      console.error("❌ Failed to import socket:", err);
    }
  };

  const updateUserName = ({ firstName, lastName }) => {
    if (!user) return;
    setUser({ ...user, firstName, lastName });
  };

  const updateUserEmail = (newEmail) => {
    if (!user) return;
    setUser({ ...user, email: newEmail });
  };

  const signup = async ({ firstName, lastName, email, password, role }) => {
    try {
      console.log("📝 SIGNUP START - Email:", email, "Role:", role);
      
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName, email, password, role }),
      });

      console.log("Response status:", res.status);

      const data = await res.json();
      console.log("Response data:", data);

      if (!res.ok) {
        console.error("❌ Signup error:", data.error);
        return { success: false, message: data.error || "Signup failed" };
      }

      // ✅ CREATE USER OBJECT WITH ROLE
      const newUser = {
        user_id: data.user_id || Date.now(),
        firstName,
        lastName,
        email,
        role: role?.toLowerCase() || "depositor",
      };

      console.log("✅ User created:", newUser);

      setUser(newUser);
      sessionStorage.setItem("user", JSON.stringify(newUser));
      if (data.token) sessionStorage.setItem("authToken", data.token);

      connectSocketWhenReady();

      return { success: true, user: newUser };
    } catch (err) {
      console.error("❌ Signup error:", err);
      return { success: false, message: "Network error" };
    }
  };

  const login = async (email, password) => {
    try {
      console.log("🔐 LOGIN START - Email:", email);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.error("❌ LOGIN TIMEOUT - Request took too long");
      }, 30000);

      console.log("📤 Sending login request to https://dshb-deposits.its.uiowa.edu/api/login");
      
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log("📡 Response received - Status:", res.status);

      const data = await res.json();
      console.log("📦 Response body:", data);

      if (!res.ok) {
        console.error("❌ Login API error:", data.error);
        return { success: false, message: data.error || "Login failed" };
      }

      console.log("✅ Login successful - User:", data.user);
      
      setUser(data.user);
      sessionStorage.setItem("user", JSON.stringify(data.user));
      if (data.token) sessionStorage.setItem("authToken", data.token);
      console.log("💾 User saved to sessionStorage");

      console.log("🔌 Connecting socket...");
      await connectSocketWhenReady();

      console.log("✅ LOGIN COMPLETE - Ready to redirect");
      return { success: true, user: data.user };

    } catch (err) {
      if (err.name === "AbortError") {
        console.error("❌ LOGIN TIMEOUT - Backend took too long to respond");
        return { success: false, message: "Login timeout - backend not responding. Make sure backend is running!" };
      }
      console.error("❌ LOGIN ERROR:", err.message);
      return { success: false, message: "Network error: " + err.message };
    }
  };

  const logout = () => {
    console.log("🚪 Logging out...");
    setUser(null);
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("authToken");

    try {
      import("../socket").then(() => {
        console.log("✅ Socket disconnected");
      });
    } catch (err) {
      console.error("❌ Failed to disconnect socket:", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loadingUser,
        setUser,
        signup,
        login,
        logout,
        updateUserName,
        updateUserEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);